import React, { useState, useRef, useContext, useEffect, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Spin, Grid, Select, Modal, Descriptions, Tag } from "antd";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import "./AttendanceCalendar.css";
import ImproperLog from "../ImproperLog";


export default function LeaveCalendar({ employee }) {
    const { authData, backUrl } = useContext(AuthContext);
    const calendarRef = useRef(null);
    const containerRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const MIN_YEAR = 2026;
    const MIN_MONTH = 2;

    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const [leaveData, setLeaveData] = useState({});
    const leaveDataRef = useRef({});
    const loadingKeysRef = useRef(new Set());

    /* ---------------------------------------------- */
    /* DATE CLICK MODAL STATE                          */
    /* ---------------------------------------------- */
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDateInfo, setSelectedDateInfo] = useState(null); // { date, record }

    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();

    const employeeId =
        employee?.employee?.id ||
        employee?.id ||
        authData?.employee?.id ||
        null;

    /* ---------------------------------------------- */
    /* STATUS COLORS                                   */
    /* ---------------------------------------------- */

    const statusColors = {
        PRESENT: "#009A44",                        // dark green
        ABSENT: "#FF0000",                         // red
        "GRACE - LATE IN": "#FFFFCC",             // light mint green
        "GRACE – EARLY OUT": "#FFFFCC",           // light mint green
        "LATE IN": "#FFFFCC",                      // light yellow
        "EARLY OUT": "#FFFFCC",                    // light yellow
        "SHORTFALL PRESENT": "#007070",            // dark teal
        "HALF DAY - SHORTFALL PRESENT": "#00BFBF", // medium teal/cyan
        "SHORT HOURS": "#D4C9A8",                  // beige/tan
        "MISPUNCH OUT": "#B0B0B0",                 // medium gray
        "MISPUNCH IN": "#B0B0B0",                  // medium gray
        PERMISSION: "#88FF00",                     // bright lime green
        HOLIDAY: "#FFA040",                        // orange
        SUNDAY: "#FFFF00",                         // pure yellow
        COMPOFF: "#00AADD",                        // sky/cyan blue
        DEFAULT: "white",
        WEEKOFF: "#FFFF00",                        // pure yellow
        TECHNICAL_ISSUE: "gray",
        CASUAL: "#CC00CC",                         // magenta/purple
        SICK: "#FF1493",                           // hot pink / deep pink
        "HALF DAY - CASUAL": "#FF80C0",             // light pink
        "HALF DAY - SICK": "#9999CC",               // medium lavender/purple
    };


    /* Tag color mapping for Ant Design Tag component */
    const statusTagColor = {
        PRESENT: "success",
        ABSENT: "error",
        LEAVE: "error",
        HALF_DAY_LEAVE: "warning",
        HALFDAYLEAVE: "warning",
        PERMISSION: "cyan",
        HOLIDAY: "orange",
        SUNDAY: "gold",
        WEEKOFF: "gold",
        COMPOFF: "blue",
        LOP: "red",
        TECHNICAL_ISSUE: "default",
        DEFAULT: "default",
    };

    const statusLabel = {
        PRESENT: "Present",
        ABSENT: "Absent",
        LEAVE: "Leave",
        HALF_DAY_LEAVE: "Half Day Leave",
        HALFDAYLEAVE: "Half Day Leave",
        PERMISSION: "Permission",
        HOLIDAY: "Holiday",
        SUNDAY: "Week Off",
        WEEKOFF: "Week Off",
        COMPOFF: "Comp Off",
        LOP: "Loss of Pay",
        TECHNICAL_ISSUE: "Technical Issue",
        DEFAULT: "No Record",
    };

    const getDarkerColor = (hex) => {
        const namedToHex = {
            green: "#008000", red: "#ff0000", lightgreen: "#90ee90",
            orange: "#ffa500", yellow: "#ffff00", blue: "#0000ff",
            white: "#ffffff", gray: "#808080",
        };
        let h = namedToHex[hex] || hex;
        const amt = -60;
        let usePound = false;
        if (h[0] === "#") { h = h.slice(1); usePound = true; }
        let num = parseInt(h, 16);
        let r = Math.max(Math.min(255, (num >> 16) + amt), 0);
        let g = Math.max(Math.min(255, ((num >> 8) & 0x00ff) + amt), 0);
        let b = Math.max(Math.min(255, (num & 0x0000ff) + amt), 0);
        return (usePound ? "#" : "") + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
    };

    /* ---------------------------------------------- */
    /* FORCE SIZE RECALCULATION (modal/tab fix)        */
    /* ---------------------------------------------- */
    const updateCalendarSize = useCallback(() => {
        calendarRef.current?.getApi()?.updateSize();
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;
        const t1 = setTimeout(updateCalendarSize, 0);
        const t2 = setTimeout(updateCalendarSize, 150);
        const t3 = setTimeout(updateCalendarSize, 400);
        const observer = new ResizeObserver(updateCalendarSize);
        observer.observe(containerRef.current);
        return () => {
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
            observer.disconnect();
        };
    }, [updateCalendarSize]);

    /* ---------------------------------------------- */
    /* YEAR OPTIONS                                    */
    /* ---------------------------------------------- */
    const yearOptions = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i)
        .filter((y) => y >= MIN_YEAR);

    /* ---------------------------------------------- */
    /* LOAD LEAVES                                     */
    /* ---------------------------------------------- */
    const loadLeaves = useCallback(async (month, year) => {
        if (!employeeId) return;
        const key = `${year}-${month}`;
        if (leaveDataRef.current[key] !== undefined) return;
        if (loadingKeysRef.current.has(key)) return;

        loadingKeysRef.current.add(key);
        setLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/hr/calendar/?employee_id=${employeeId}&month=${month}&year=${year}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            const data = res.data.results || res.data || [];
            leaveDataRef.current[key] = data;
            setLeaveData((prev) => ({ ...prev, [key]: data }));
        } catch (err) {
            console.error("Error fetching leave data:", err);
            leaveDataRef.current[key] = [];
            setLeaveData((prev) => ({ ...prev, [key]: [] }));
        } finally {
            setLoading(false);
            loadingKeysRef.current.delete(key);
        }
    }, [employeeId, backUrl, authData?.access]);

    /* ---------------------------------------------- */
    /* BUILD EVENTS                                    */
    /* ---------------------------------------------- */
    const buildEvents = () => {
        const events = [];
        Object.values(leaveData).forEach((records) => {
            (records || []).forEach((item) => {
                const date = item?.Date || item?.date;
                if (!date) return;

                const status = item?.AttendanceStatus?.toUpperCase?.() || "DEFAULT";
                const leaveType = item?.LeaveType || null;
                const remarks = item?.StatusMessage?.toUpperCase?.() || "DEFAULT";

                events.push({
                    start: date,
                    display: "background",
                    backgroundColor: statusColors[remarks] || statusColors.DEFAULT,
                    borderColor: getDarkerColor(statusColors[remarks] || statusColors.DEFAULT),
                    extendedProps: { status, secondary: leaveType, remarks },
                });
            });
        });
        return events;
    };

    /* ---------------------------------------------- */
    /* EVENT CONTENT                                   */
    /* ---------------------------------------------- */

    const eventContent = (eventInfo) => {
        const { status, secondary, remarks } = eventInfo.event.extendedProps;
        const displayText = {
            PRESENT: "Present", ABSENT: "Absent", LEAVE: "Leave",
            HALF_DAY_LEAVE: "Half", HALFDAYLEAVE: "Half", PERMISSION: "Perm", HALFDAY_LEAVE: "Half",
            HOLIDAY: "Holiday", SUNDAY: "WeekOff", COMPOFF: "CompOff",
            WEEKOFF: "Weekoff", LOP: "LOP", TECHNICAL_ISSUE: "TI",
        }[status] || "-";


        return (
            <div className="fc-event-wrapper">
                <div
                    className="fc-event-top"
                    style={{ backgroundColor: statusColors[remarks] || statusColors.DEFAULT, opacity: 1 }}
                />
                <div className="fc-event-bottom">
                    <span className="primary">
                        {
                            secondary && (
                                status === "LEAVE" ||
                                status === "HALFDAYLEAVE" ||
                                remarks === "HALF DAY - SICK" ||
                                remarks === "HALF DAY - CASUAL"
                            )
                                ? (
                                    status === "HALFDAYLEAVE" && secondary === "Sick" ? "HD - SL" :
                                        status === "HALFDAYLEAVE" && secondary === "Casual" ? "HD - CL" :
                                            status === "LEAVE" && secondary === "Casual" ? "CL" :
                                                "SL"
                                )
                                : displayText
                        }
                    </span>
                </div>
            </div>
        );
    };

    /* ---------------------------------------------- */
    /* DATE CLICK HANDLER                              */
    /* ---------------------------------------------- */
    const handleDateClick = useCallback((info) => {
        const clickedDate = info.dateStr; // "YYYY-MM-DD"
        console.log("Date clicked:", clickedDate);

        // Prevent modal for future dates (parse as local date to avoid UTC offset issues)
        const today = new Date();
        const [y, m, d] = clickedDate.split("-").map(Number);
        const clicked = new Date(y, m - 1, d); // local midnight
        if (clicked > today) return;

        // Find the record for this date across all loaded data
        let matchedRecord = null;
        for (const records of Object.values(leaveDataRef.current)) {
            const found = (records || []).find((item) => {
                const d = item?.Date || item?.date;
                return d === clickedDate;
            });
            if (found) { matchedRecord = found; break; }
        }

        setSelectedDateInfo({ date: clickedDate, record: matchedRecord });
        setModalOpen(true);
    }, []);

    /* ---------------------------------------------- */
    /* SHARED NAVIGATE HELPER                          */
    /* ---------------------------------------------- */
    const navigateTo = useCallback((month, year) => {
        let safeYear = year;
        let safeMonth = month;
        if (safeYear < MIN_YEAR || (safeYear === MIN_YEAR && safeMonth < MIN_MONTH)) {
            safeYear = MIN_YEAR;
            safeMonth = MIN_MONTH;
        }
        calendarRef.current?.getApi()?.gotoDate(
            `${safeYear}-${String(safeMonth).padStart(2, "0")}-01`
        );
        setCurrentMonth(safeMonth);
        setSelectedYear(safeYear);
        const key = `${safeYear}-${safeMonth}`;
        if (leaveDataRef.current[key] === undefined) {
            loadLeaves(safeMonth, safeYear);
        }
    }, [loadLeaves]);

    /* ---------------------------------------------- */
    /* HANDLE PREV/NEXT BUTTON CHANGES                 */
    /* ---------------------------------------------- */
    const handleDatesSet = useCallback((info) => {
        const start = info.view.currentStart;
        const newMonth = start.getMonth() + 1;
        const newYear = start.getFullYear();

        if (newYear < MIN_YEAR || (newYear === MIN_YEAR && newMonth < MIN_MONTH)) {
            calendarRef.current?.getApi()?.gotoDate(
                `${MIN_YEAR}-${String(MIN_MONTH).padStart(2, "0")}-01`
            );
            return;
        }

        setCurrentMonth(newMonth);
        setSelectedYear(newYear);

        const key = `${newYear}-${newMonth}`;
        if (leaveDataRef.current[key] === undefined) {
            loadLeaves(newMonth, newYear);
        }
    }, [loadLeaves]);

    /* ---------------------------------------------- */
    /* YEAR DROPDOWN CHANGE                            */
    /* ---------------------------------------------- */
    const handleYearChange = useCallback((year) => {
        navigateTo(currentMonth, year);
    }, [currentMonth, navigateTo]);

    /* ---------------------------------------------- */
    /* INITIAL LOAD                                    */
    /* ---------------------------------------------- */
    useEffect(() => {
        if (!employeeId) return;
        const m = new Date().getMonth() + 1;
        const y = new Date().getFullYear();
        setCurrentMonth(m);
        setSelectedYear(y);
        loadLeaves(m, y);
    }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ---------------------------------------------- */
    /* MODAL CONTENT HELPERS                           */
    /* ---------------------------------------------- */
    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    };

    const getModalRecord = () => {
        if (!selectedDateInfo) return null;
        const { record } = selectedDateInfo;
        if (!record) return null;
        const status = record?.AttendanceStatus?.toUpperCase?.() || "DEFAULT";
        const remarks = record?.StatusMessage
        return { status, record, remarks };
    };

    /* ---------------------------------------------- */
    /* RENDER                                          */
    /* ---------------------------------------------- */
    return (
        <center>
            <div
                ref={containerRef}
                className={`leave-calendar-container ${screens.xs ? "xs" : screens.sm ? "sm" : "lg"}`}
            >
                {/* ------------------ YEAR FILTER ------------------ */}
                <div style={{ marginBottom: 12 }}>
                    <label>YEAR : </label>
                    <Select
                        value={selectedYear}
                        style={{ width: 130 }}
                        onChange={handleYearChange}
                    >
                        {yearOptions.map((y) => (
                            <Select.Option key={y} value={y}>{y}</Select.Option>
                        ))}
                    </Select>
                </div>

                {/* ------------------ CALENDAR ------------------ */}
                {loading && Object.keys(leaveData).length === 0 ? (
                    <div className="leave-calendar-loading">
                        <Spin tip="Loading Leave Calendar..." />
                    </div>
                ) : (
                    <>
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[dayGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            headerToolbar={{
                                left: screens.xs ? "prev,next" : "prev,next today",
                                center: "title",
                                right: "",
                            }}
                            height="100%"
                            events={buildEvents()}
                            eventContent={eventContent}
                            eventDisplay="block"
                            displayEventTime={false}
                            datesSet={handleDatesSet}
                            dateClick={handleDateClick}
                            selectable={true}
                            dayCellClassNames={(arg) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                return arg.date > today ? ["fc-day-future-disabled"] : [];
                            }}
                        />

                        {loading && (
                            <div style={{ textAlign: "center", marginTop: 8, color: "#888", fontSize: 13 }}>
                                <Spin size="small" />&nbsp; Loading...
                            </div>
                        )}

                        {/* ---------- LEGEND SECTION ---------- */}
                        <div
                            style={{
                                marginTop: 20,
                                padding: "10px 15px",
                                background: "#ffffff",
                                borderRadius: 8,
                                width: "100%",
                                maxWidth: 600,
                                textAlign: "left",
                                boxShadow: "0 0 8px rgba(0,0,0,0.1)",
                            }}
                        >
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, 1fr)",
                                    rowGap: 8,
                                    columnGap: 10,
                                }}
                            >
                                {[
                                    { label: "Present", color: "#009A44" },
                                    { label: "Permission", color: "#88FF00" },           // Dark Green
                                    // { label: "Grace Late In / Early Out", color: "#CCFFCC" }, // Amber
                                    { label: "Late In / Early Out", color: "#FFFFCC" },  // Dark Orange
                                    { label: "Short Fall - Present", color: "#007070" }, // Purple
                                    { label: "HalfDay Short Fall - Present", color: "#00BFBF" },
                                    { label: "Week Off", color: "#FFFF00" },             // Soft Yellow
                                    { label: "Comp Off", color: "#00AADD" },             // Blue
                                    { label: "Sick", color: "#FF1493" },
                                    { label: "Casual", color: "#CC00CC" },               // Lavender
                                    { label: "Half Day - Sick Leave", color: "#9999CC" },        // Dark Turquoise
                                    { label: "Half Day - Casual Leave", color: "#FF80C0" },      // Pink
                                    { label: "Short Hours", color: "#D4C9A8" },          // Brown
                                    { label: "Mispunch Out / IN", color: "#B0B0B0" },    // Grey
                                    { label: "Absent", color: "#FF0000" },               // Strong Red
                                    { label: "Holiday", color: "#FFA040" }               // Deep Orange
                                ].map((item) => (
                                    <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span
                                            style={{
                                                width: 18,
                                                height: 18,
                                                background: item.color,
                                                border: "1px solid #464343",
                                                display: "inline-block",
                                            }}
                                        />
                                        <span>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* ================================================ */}
            {/* DATE DETAIL MODAL                                 */}
            {/* ================================================ */}
            <Modal
                title={
                    <>
                        <h6>Attendance Log</h6>
                        <span style={{ fontSize: 16, fontWeight: 600 }}>
                            {selectedDateInfo ? formatDate(selectedDateInfo.date) : ""}
                        </span>
                    </>

                }
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                footer={null}
                centered
                width={520}
                destroyOnClose
            >
                {(() => {
                    const info = getModalRecord();
                    const status = info?.status || "DEFAULT";
                    const record = info?.record;
                    const remarks = info?.remarks

                    console.log("record :", record)

                    return (
                        <div style={{ padding: "8px 0" }}>
                            {/* Status badge */}
                            <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ fontWeight: 500, color: "#555" }}>Status:</span>
                                <Tag
                                    color={statusTagColor[status] || "default"}
                                    style={{ fontSize: 13, padding: "2px 12px", borderRadius: 5 }}
                                >
                                    {status || "No Record"}
                                </Tag>
                                <span style={{ fontWeight: 500, color: "#555" }}>Remarks:</span>
                                <Tag
                                    color={statusTagColor[status] || "default"}
                                    style={{ fontSize: 13, padding: "2px 12px", borderRadius: 5 }}
                                >
                                    {remarks === "Grace - Early Out"
                                        ? "Early Out"
                                        : remarks === "Grace - Late In"
                                            ? "Late In"
                                            : remarks || "No Record"}
                                </Tag>
                            </div>

                            <ImproperLog record={record} employee={employee} />
                        </div>
                    );
                })()}
            </Modal>
        </center>
    );
}